import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { Subject, Subscription, throttleTime } from 'rxjs'
import {
  DashboardComponent,
  DashboardUserAction,
} from '../dashboard/dashboard.component'
import {
  BiddingGame,
  biddingGameNull,
  BiddingService,
  BidType,
  Card,
} from 'game-services'
import { playerScoreCardNull } from 'game-play-services'

@Component({
  selector: 'sans-bidding',
  standalone: true,
  imports: [DashboardComponent],
  templateUrl: './bidding.component.html',
  styleUrl: './bidding.component.scss',
})
export class BiddingComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public biddingGame: BiddingGame = biddingGameNull
  @Input() public userPlayerScoreCard = playerScoreCardNull
  @Input() public leftOpponentPlayerScoreCard = playerScoreCardNull
  @Input() public rightOpponentPlayerScoreCard = playerScoreCardNull

  @Output() public gameUpdated = new EventEmitter<void>()

  public passBidLabel = $localize`:Bidding component|Pass bid label:Pass`
  private passBidValue = 'pass'

  public talonCards: Card[] = []
  public userCards: Card[] = []
  public rightOpponentCards: Card[] = []
  public leftOpponentCards: Card[] = []

  public passBidClicks = new Subject<void>()
  public placeBidClicks = new Subject<BidType>()

  public passBidClicksSubscription = new Subscription()
  public placeBidClicksSubscription = new Subscription()

  public shouldShowPassBidOption = false
  public bidOptions: BidOption[] = []
  public userActions: DashboardUserAction[] = []

  public constructor(private readonly biddingService: BiddingService) {}

  public async ngOnInit(): Promise<void> {
    this.passBidClicksSubscription = this.passBidClicks
      .pipe(throttleTime(1000))
      .subscribe(async () => {
        await this.biddingService.passBid(this.biddingGame.gamePlayGameId)
        this.gameUpdated.emit()
      })

    this.placeBidClicksSubscription = this.placeBidClicks
      .pipe(throttleTime(1000))
      .subscribe(async (bidType) => {
        await this.biddingService.placeBid(
          this.biddingGame.gamePlayGameId,
          bidType,
        )
        this.gameUpdated.emit()
      })
  }

  public ngOnChanges(): void {
    this.shouldShowPassBidOption = this.biddingGame.shouldShowPassBidOption

    this.bidOptions = this.biddingGame.userBidOptions.map((bid) => {
      return {
        bidType: bid.bidType,
        bidName: bid.getBidName(),
      }
    })

    const userActionsFromBidOptions = this.bidOptions.map((bidOption) => {
      return {
        buttonLabel: bidOption.bidName,
        value: bidOption.bidType,
      }
    })

    this.userActions = [
      { buttonLabel: this.passBidLabel, value: this.passBidValue },
    ].concat(userActionsFromBidOptions)

    this.talonCards = this.biddingGame.getTalonCards()
    this.userCards = this.biddingGame.user.getCards()
    this.rightOpponentCards = this.biddingGame.rightOpponent.originalCards
    this.leftOpponentCards = this.biddingGame.leftOpponent.originalCards
  }

  public ngOnDestroy(): void {
    this.passBidClicksSubscription.unsubscribe()
    this.placeBidClicksSubscription.unsubscribe()
  }

  public async onUserActionSelected(
    userAction: DashboardUserAction,
  ): Promise<void> {
    if (userAction.value === this.passBidValue) {
      this.passBidClicks.next()
      return
    }

    this.placeBidClicks.next(userAction.value as BidType)
  }
}

interface BidOption {
  readonly bidType: BidType
  readonly bidName: string
}
